import webuiState from "../app/InternalStateService";
import Url from "@tinqin/tinqin-utils/src/url";
import { addQueryParams } from "../utils/urlUtils";
import { saveResponse } from "./WidgetContainer";
import { clearGlobalState } from "./Dashboard";
import { constructRequestEntity } from "../utils/dataUtils";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

export const SET_GLOBAL_CONFIG = "SET_GLOBAL_CONFIG";
export const SET_APPLICATION_BOOT = "SET_APPLICATION_BOOT";

export const SET_APPLICATION_UPDATE_MODE = "SET_APPLICATION_UPDATE_MODE";

export function loadAppBoot() {
    return async (dispatch) => {
        const triggerApplicationLoading = webuiState.getActionByName(
            "loading.triggerApplicationLoading"
        );
        dispatch(triggerApplicationLoading());
        dispatch(setApplicationBoot(true));
        try {
            await dispatch(
                webuiState.getActionByName("request.request")({
                    method: "GET",
                    endpoint: getAppBootUrl()
                })
            );

            await webuiState.getActionByName("locale.loadStaticTranslation")(dispatch);
            return Promise.resolve();
        } catch (error) {
            const triggerMessages = webuiState.getActionByName("messages.triggerErrorMessage");
            return dispatch(
                triggerMessages({
                    type: "error",
                    message: "Application failed to boot!"
                })
            );
        } finally {
            dispatch(setApplicationBoot(false));
            setTimeout(() => {
                dispatch(triggerApplicationLoading(false));
            }, 50);
        }
    };
}

export function loadBootDashboard(preloaded = null) {
    return async (dispatch) => {
        const triggerApplicationLoading = webuiState.getActionByName(
            "loading.triggerApplicationLoading"
        );
        dispatch(setApplicationBoot(true));
        dispatch(triggerApplicationLoading());
        try {
            await webuiState.getActionByName("locale.loadStaticTranslation")(dispatch);
            let action = preloaded;
            if (!preloaded) {
                action = await buildBootDashboardAction();
            }
            await dispatch(webuiState.getActionByName("siren.dispatchSirenActions")([action]));
        } finally {
            setTimeout(() => {
                dispatch(triggerApplicationLoading(false));
            }, 50);
            dispatch(setApplicationBoot(false));
        }
    };
}

export function requestFile(sirenAction, currentWidgetId, currentPath, uri, target = "_blank") {
    return async (dispatch, getState) => {
        let {
            //default headers
            headers = {
                "Content-Type": sirenAction.type || sirenAction.contentType || "application/json"
            },
            properties = {}
        } = sirenAction;

        let actionResult;
        let currentWidgetEntity = getState().getIn(["data", currentWidgetId, "class", 0]);

        //if no sources provided - default is current widget
        //if sources is provided, but it is an empty array - no content will be send to the server
        //if sources is provided - requested content will be send to the server
        let sources = [currentWidgetEntity];
        if (sirenAction.hasOwnProperty("sources")) {
            sources = sirenAction.sources.length ? sirenAction.sources : [];
        }

        let requestPayload = constructRequestEntity(
            sources,
            getState(),
            currentPath,
            sirenAction,
            currentWidgetEntity
        );

        try {
            let actionResult = await dispatch(
                webuiState.getActionByName("request.request")(
                    {
                        method: "POST",
                        endpoint: uri,
                        payload: requestPayload,
                        headers: Object.assign({}, headers, {
                            "Cache-Control": "no-cache",
                            Pragma: "no-cache",
                            "Content-Language": getState().getIn(["global", "locale"])
                        })
                    },
                    {
                        widgetId: currentWidgetId
                    }
                )
            );
            if (actionResult.headers && actionResult.url) {
                if (!actionResult.ok) {
                    return Promise.reject(actionResult);
                }

                let headers = actionResult.headers;
                //get the headers' content disposition
                let contentDisposition = headers.get("content-disposition") || "";
                //determine the content type from the header or default to octet stream
                let contentType = headers.get("content-type") || "application/octet-stream";

                //get the file name with regex
                //match - content-disposition: inline;  filename*=UTF-8''newOffer.pdf
                //content-disposition: attachment; filename=content.txt
                let regex = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;
                let match = regex.exec(contentDisposition);

                //is there a file name
                let fileName = (match && match[3]) || "fileName";

                fileName = fileName.replace(/"/g, "");

                try {
                    actionResult.arrayBuffer().then((data) => {
                        let blob = new Blob([data], { type: contentType });
                        let sizeInBytes = blob.size;
                        if (!sizeInBytes) {
                            return Promise.resolve();
                        }
                        //downloading the file depends on the browser
                        //IE handles it differently than chrome/webkit
                        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                            window.navigator.msSaveOrOpenBlob(blob, fileName);
                        } else {
                            openFileLink({
                                blob,
                                target,
                                fileName,
                                downloadFlag: !contentDisposition.includes("inline"),
                                cleanupDelay: properties.cleanupDelay,
                                windowFeatures: properties.windowFeatures
                            });
                        }
                    });
                } catch (error) {
                    WEBUILogs.error("Save Blob method failed with the following exception.");
                    WEBUILogs.error(error);
                }
            } else {
                let currentWidgetEntity = getState().getIn(["data", currentWidgetId, "class", 0]);
                let targets = [currentWidgetEntity];
                if (sirenAction.hasOwnProperty("targets")) {
                    targets = sirenAction.targets.length ? sirenAction.targets : [];
                }
                await dispatch(
                    saveResponse(actionResult, targets, getState(), currentPath, currentWidgetId)
                );
            }
        } catch (response) {
            actionResult = Promise.reject(response);
        }
        return actionResult;
    };
}

function openFile(file, target, cleanupDelay, windowFeatures) {
    //Base64 content
    const byteCharacters = atob(file.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const contentType = file.contentType || "application/pdf;charset=utf-8"; //"application/octet-stream";
    const blob = new Blob([byteArray], { type: contentType });
    const sizeInBytes = blob.size;
    if (!sizeInBytes) {
        return Promise.resolve();
    }
    //downloading/opening the file depends on the browser
    //IE handles it differently than webkit
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, file.name || "fileName");
        return;
    }
    //"inline" indicates if the content is expected to be displayed inline or in the browser
    //"attachment", means that it should be downloaded and saved locally
    const contentDisposition = file.contentDisposition || "inline";
    const downloadFlag = !contentDisposition.includes("inline");
    const fileName = file.name;
    openFileLink({ target, blob, downloadFlag, fileName, cleanupDelay, windowFeatures });
}

//incorrect method naming, preserved for backward-compatibility
// action for opening new window/tab based on properties.href
// if properties.target === "_window" will open a new window, otherwise will open a new tab and use target as the name of the tab (_blank by default)
// if file is added to the action description (as a base64 string):
// example: properties: {file: { name, content: "base64string", contentDisposition: "inline|attachement"}}
// name used for the downloaded name, contentDisposition for whether to open the document or download it
// opens a file in new window/tab or can download a file
// if action.method is POST, sends a POST request and expects as a result byte[]
// with a file to open in new window/tab or download it
export function openNewTab(action = {}, widget, path) {
    return (dispatch) => {
        let properties = action.properties || {};
        let type = properties.type || "internal";
        let target = properties.target || "_blank";

        //the file content is already generated
        //there is no need to make server requests
        if (properties.file) {
            return openFile(
                properties.file,
                target,
                properties.cleanupDelay,
                properties.windowFeatures
            );
        }

        let queryParameters = action.parameters || {};
        //tye: "external" - for example: href: "mailto:..."
        let uri = `${action.href}`;
        if (type === "internal") {
            let { API_ROOT } = webuiState.getWebConfig();
            uri = `${API_ROOT}${action.href}`;
        } else if (type === "static") {
            let { ROOT_PATH_NO_API } = webuiState.getWebConfig();
            uri = `${ROOT_PATH_NO_API}${action.href}`;
        }
        if (action.method && action.method === "POST") {
            return dispatch(
                webuiState.getActionByName("application.requestFile")(
                    action,
                    widget,
                    path,
                    uri,
                    target
                )
            );
        }
        uri = addQueryParams(uri, queryParameters);
        //using "_blank" as the target attribute value will create several new and unnamed tabs/windows
        if (target === "_window") {
            //Specifying window "features" to the "open" call,
            //force the browser to open a new window, instead of the default behavior of opening a new tab
            window.open(uri, "_blank", "location=yes");
            return;
        }
        window.open(uri, target);
    };
}

//to be deprecated
export function openNewTabRoot(action = {}) {
    return () => {
        //check for full url with no /api
        let { ROOT_PATH_NO_API } = webuiState.getWebConfig();
        window.open(`${ROOT_PATH_NO_API}${action.href}`, "_blank");
    };
}

// creates a link for a file and clicks on it
// this can trigger widnow.open()
// or can just download the file
/*
    https://github.com/tinqin-software/tinqin-ui/pull/1092
    Some browsers may block window.open() calls if they are not triggered by user interactions, such as mouse clicks or keyboard events. This is often done for security reasons to prevent pop-ups and other unwanted behavior.
    In our code we trigger window.open programatically with element.click(), which is not recognized by some browser as user interaction.
    Workaround is a check for safari userAgent version < 16, and if this is the case we do not add click listener with window.open and rely on browsers native behavior.
    Tested in browserstack.com on Safari 15.* the file is opened in the same tab, and in below 15 it shows download prompt.
    Tested on Safari 16, 15, 14, 13 with 3 seconds delay of the server response and is OK. In version 12.* it does not work because of "WebKitBlobResourse error 1" but we need to support versions >=13
*/
function openFileLink({
    //input file as a blob
    blob,
    //target: if equals "_window" new window will be opened with the file in it
    //if different than "_window", target will be used an identification for the new tab
    // if left as "_blank", each new opened file will create a new tab in the browser
    target = "_blank",
    //options for window.open if new window is to be opened (like position and dimensions)
    //more info at: https://developer.mozilla.org/en-US/docs/Web/API/Window/open#window_features
    windowFeatures,
    //
    fileName = "fileName",
    //downloadFlag: if true, will skip window.open and only download the file when the link is clicked
    downloadFlag = false,
    //how long to keep object reference in the browser
    //references are cleaned up on document unload from the browser
    // defaults to 1 hour
    cleanupDelay = 3600000
} = {}) {
    const link = document.createElement("a");
    link.target = "_blank";
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.style.display = "none";

    // For Safari version below 16 we cannot open blob in new tab, so open it in the same, version below 15 shows download prompt (tested in browserStack.com)
    const isOldSafari = () => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const safariVersionMatch = navigator.userAgent.match(/Version\/([\d.]+)/);
        const safariVersion = safariVersionMatch ? safariVersionMatch[1] : undefined;
        return isSafari && safariVersion && parseFloat(safariVersion) < 16;
    };

    if (!downloadFlag && !isOldSafari()) {
        link.onclick = function () {
            //Specifying "windowFeatures" to the "open" call,
            //forcing the browser to open a new window, instead of the default behavior of opening a new tab
            //window.open will open new window if third argument is a non empty string
            if (target === "_window") {
                window.open(link.href, "_blank", windowFeatures ? windowFeatures : "location=yes");
            } else {
                window.open(link.href, target);
            }
            return false;
        };
    }
    //Workaround for iOS Safari issue with window.open from async requests
    //Safari blocks all window.open requests if that request is made from an async call
    //using this method (setTimeout) we force the browser to run window.open on the main thread.
    //more info: https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
    setTimeout(() => {
        link.click();
        //when downloading the file, it is ok to revoke the object immediately
        if (downloadFlag) {
            URL.revokeObjectURL(link.href);
        } else {
            //if opened in another tab, revoking the object too soon will result in user not able to download the file
            //default cleanupDelay = 1 hour
            //object are revoked automatically on document unload
            //more info: https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL#memory_management
            if (cleanupDelay !== false) {
                setTimeout(() => {
                    URL.revokeObjectURL(link.href);
                }, cleanupDelay);
            }
        }
    }, 100);
}

function getAppBootUrl() {
    let { BOOT } = webuiState.getWebConfig();
    let queryStart = BOOT.indexOf("?");
    let bootAddress = BOOT;
    let bootQuery = "";
    if (queryStart > -1) {
        bootAddress = BOOT.substring(0, queryStart);
        bootQuery = BOOT.substring(queryStart);
    }
    if (window.location.search !== "") {
        //get current query string as object
        let currentParams = Url.formatQueryString(window.location.search.slice(1));
        //set current query params to boot query
        bootQuery = Url.updateUrlParameters(bootQuery, currentParams);
    }

    if (bootQuery !== "") {
        return bootAddress + bootQuery;
    } else return BOOT;
}

function buildBootDashboardAction() {
    let query = Url.formatQueryString(window.location.search.slice(1));
    let dashboardQueryName = webuiState.getGlobalProperties("dashboardQueryName") || "context";

    query.dashboard =
        query[dashboardQueryName] || webuiState.getGlobalProperties("initialDashboard");
    query.object = query.object || webuiState.getGlobalProperties("initialObject");
    let staticApi = query.dashboard.startsWith(
        webuiState.getGlobalProperties("staticApiDashboardIdPrefix")
    );
    let prefix = staticApi ? webuiState.getGlobalProperties("staticApiPublicPath") : "";
    let initialHrefParser = webuiState.getGlobalProperties("initialHrefParser");
    //function to be initialize on project level and returning an action
    if (initialHrefParser && typeof initialHrefParser === "function") {
        return initialHrefParser(prefix, query);
    }

    let href = `${prefix}/widget/${query.dashboard}`;
    let parameters = {};
    //project specific url generation
    if (webuiState.hasAction("application.buildBootDashboardAction")) {
        return webuiState.getActionByName("application.buildBootDashboardAction")({
            prefix: `${prefix}/widget/`
        });
    }

    return {
        actionType: "switchContainer",
        href: href,
        method: "GET",
        contentType: "application/json",
        targets: ["{root}"],
        parameters
    };
}

export function setApplicationUpdateMode(updateMode = true) {
    return {
        type: SET_APPLICATION_UPDATE_MODE,
        payload: {
            updateMode
        }
    };
}

export function redirect(sirenAction) {
    return (window.location = sirenAction.href);
}

function setGlobalConfig(newState) {
    return {
        type: SET_GLOBAL_CONFIG,
        payload: newState
    };
}

export function setApplicationBoot(newState) {
    return {
        type: SET_APPLICATION_BOOT,
        payload: newState
    };
}

export function loadApplicationConfig(sirenAction) {
    return async (dispatch) => {
        let actionResult;
        try {
            let actionResult = await dispatch(
                webuiState.getActionByName("request.request")({
                    endpoint: sirenAction.href,
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                })
            );
            await dispatch(setGlobalConfig(actionResult));
        } catch (error) {
            actionResult = Promise.reject(error);
        }
        return actionResult;
    };
}

export function getStoreByName(name) {
    return (dispatch, getState) => {
        return getState().get(name);
    };
}

export function reboot() {
    return async (dispatch) => {
        dispatch(clearGlobalState(true));
        if (webuiState.getGlobalProperties("skipAppBoot")) {
            return dispatch(loadBootDashboard());
        } else {
            return dispatch(loadAppBoot());
        }
    };
}
